/**
 * Formats a number as currency
 * @param {number} amount - The amount to format
 * @param {string} currency - Currency code (default: 'USD')
 * @param {string} locale - Locale for formatting (default: 'en-US')
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount, currency = 'USD', locale = 'en-US') => {
  // Coerce numeric strings (e.g. Postgres numeric columns) to a number.
  const numeric = typeof amount === 'string' ? Number(amount) : amount;

  // Never throw on bad input — a single null/undefined/NaN value must not crash
  // the page. Fall back to a safe zero amount instead.
  if (typeof numeric !== 'number' || !Number.isFinite(numeric)) {
    return '$0.00';
  }

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(numeric);
};

/**
 * Formats a number as currency without the currency symbol
 * @param {number} amount - The amount to format
 * @param {string} locale - Locale for formatting (default: 'en-US')
 * @returns {string} Formatted number string
 */
export const formatCurrencyAmount = (amount, locale = 'en-US') => {
  // Coerce numeric strings (e.g. Postgres numeric columns) to a number.
  const numeric = typeof amount === 'string' ? Number(amount) : amount;

  // Never throw on bad input — a single null/undefined/NaN value must not crash
  // the page. Fall back to a safe zero amount. No currency symbol, matching this
  // function's format.
  if (typeof numeric !== 'number' || !Number.isFinite(numeric)) {
    return '0.00';
  }

  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(numeric);
};

/**
 * Parses a currency string back to a number
 * @param {string} currencyString - The currency string to parse
 * @param {string} locale - Locale used for formatting (default: 'en-US')
 * @returns {number} Parsed number
 */
export const parseCurrency = (currencyString, locale = 'en-US') => {
  if (typeof currencyString !== 'string') {
    throw new Error('Currency string must be a string');
  }

  // Remove currency symbols and grouping separators
  const numberString = currencyString
    .replace(/[^\d.,-]/g, '')
    .replace(/,/g, '');

  const parsed = parseFloat(numberString);
  
  if (isNaN(parsed)) {
    throw new Error('Invalid currency format');
  }

  return parsed;
};

/**
 * Gets currency symbol for a given currency code
 * @param {string} currency - Currency code
 * @returns {string} Currency symbol
 */
export const getCurrencySymbol = (currency = 'USD') => {
  try {
    return (0).toLocaleString('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).replace(/\d/g, '').trim();
  } catch (error) {
    return currency;
  }
};
